import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    orderBy,
    query,
    setDoc,
    Timestamp,
    updateDoc,
    where
} from 'firebase/firestore';
import { db } from './firebase';
import { Producteurs } from '../models/Producteurs';
import { Paniers, PaniersConverter } from '../models/Paniers';
import { IndicatorViewModel } from '../models/IndicatorsViewModal';
import { Commandes, CommandesConverter } from '../models/Commandes';
import { SalesTableViewData } from '../models/SalesTableViewData';
import {
    getAverageAmount,
    getDateFromTimestamp,
    getLatestDate,
    getReviewDetails,
    getTotalAmount,
    isDateInThisMonth,
    IsDateWithin24Hours,
    isDayIncluded,
    sortByDate,
    sortOrderTable,
    weeksBetween
} from '../utils';
import {
    MessagesDeProducteurs,
    MessagesDeProducteursConverter
} from '../models/MessagesDeProducteurs';
import { Clients, ClientsConverter } from '../models/Clients';
import { amosubscription, deleteImageFile, uploadImage } from './storageFunctions';
import { BasketIndicatorViewModel } from '../models/BasketIndicatorsViewModal';
import { Collections } from '../models/Collections';
import { ClientTableData } from '../models/ClientTableData';
import { Satisfaction } from '../models/Satisfaction';
import { PartagePanier } from '../models/PartagePanier';
import { OrderSatisfaction } from '../models/OrderSatisfaction';
import { GroupOrderIds } from '../models/GroupOrder';
import { GroupMemberStatus } from '../models/groupMemberStatus';
import { Status } from '../models/Status';
import { TFunction } from 'i18next';
import { BasketDetails } from '../models/BasketDetails';
import { BasketGroupData } from '../models/BasketGroupData';

export const getProducer = async (uid: string): Promise<Producteurs | null> => {
    const docRef = doc(db, 'Producteurs', uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return docSnap.data() as Producteurs;
    } else {
        return null;
    }
};

export const addProducer = async (data: Producteurs): Promise<void> => {
    if (data.idProducteur) {
        const docRef = doc(db, 'Producteurs', data.idProducteur);
        await setDoc(docRef, data);
    }
};

export const getBaskets = async (uid: string): Promise<Paniers[]> => {
    const paniersList: Paniers[] = [];
    const querySnapshot = await getDocs(collection(db, 'Paniers').withConverter(PaniersConverter));
    querySnapshot.forEach((docSnap) => {
        if (docSnap.data() && docSnap.data().idProducteur === uid) {
            paniersList.push(docSnap.data());
        }
    });
    return paniersList;
};

export const getSpecificBasket = async (id: string): Promise<Paniers | void> => {
    const docRef = doc(db, 'Paniers', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return docSnap.data() as Paniers;
    }
};

export const getAllOrders = async (): Promise<Commandes[]> => {
    const list: Commandes[] = [];
    const querySnapshot = await getDocs(
        collection(db, 'Commandes').withConverter(CommandesConverter)
    );
    querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data) {
            list.push(data);
        }
    });
    return list;
};

export const getOrders = async (uid: string): Promise<Commandes[]> => {
    const orderList: Commandes[] = [];
    const list: Commandes[] = await getAllOrders();
    await Promise.all(
        list.map(async (data) => {
            if (data.fermeId === uid) {
                orderList.push(data);
            }
        })
    );
    return orderList;
};

export const getClientName = async (clientId: string): Promise<string | void> => {
    const docRef = doc(db, 'Clients', clientId);
    const docSnap = await getDoc(docRef.withConverter(ClientsConverter));
    if (docSnap.exists()) {
        const data = docSnap.data();
        return `${data.prenom} ${data.nom}`;
    }
};

export const getSpecificOrder = async (id: string): Promise<Commandes | void> => {
    const docRef = doc(db, 'Commandes', id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
        return docSnap.data() as Commandes;
    }
};

export const getClient = async (
    order: Commandes
): Promise<{ client: string[]; noOfBaskets: number; turnover: number }> => {
    const clientList: string[] = [];
    let basketCount = 0;
    let turnover = 0;
    if (order.clientId) {
        const client = await getClientName(order.clientId);
        if (client) {
            clientList.push(client);
        }
    }
    if (order.groupMemberOrderIds) {
        await Promise.all(
            order.groupMemberOrderIds?.map(async (item) => {
                const orderData = await getSpecificOrder(item.orderId);
                if (orderData) {
                    if (orderData.clientId) {
                        const client = await getClientName(orderData.clientId);
                        if (client) {
                            clientList.push(client);
                        }
                    }
                    if (orderData.baskets) {
                        basketCount += orderData.baskets.length;
                        orderData.baskets.forEach((basket) => {
                            turnover += basket.prix * basket.quantite;
                        });
                    }
                }
            })
        );
    }
    return { client: clientList, noOfBaskets: basketCount, turnover };
};

export const getOrdersTableData = async (orderData: Commandes[]): Promise<SalesTableViewData[]> => {
    const viewListOne: SalesTableViewData[] = [];
    const viewListTwo: SalesTableViewData[] = [];
    await Promise.all(
        orderData.map(async (data) => {
            let sum = 0;
            let basketCount = 0;
            data.baskets?.forEach((item) => {
                sum += item.prix * item.quantite;
                basketCount += item.quantite * 1;
            });
            if (data.groupMemberOrderIds) {
                await Promise.all(
                    data.groupMemberOrderIds?.map(async (item) => {
                        const order = await getSpecificOrder(item.orderId);

                        if (order) {
                            order.baskets?.forEach((i) => {
                                basketCount += i.quantite * 1;
                            });
                        }
                    })
                );
            }
            const { client, noOfBaskets, turnover } = await getClient(data);
            if (data.orderCompleted) {
                viewListOne.push({
                    id: data.orderId,
                    orderNumber: data.orderId,
                    date: getDateFromTimestamp(data.createdAt),
                    clients: client,
                    noOfBaskets: basketCount,
                    cost: sum + turnover,
                    status: data.orderCompleted ? 'Collecté' : 'En cours',
                    sortDate: data.createdAt
                });
            } else {
                viewListTwo.push({
                    id: data.orderId,
                    orderNumber: data.orderId,
                    date: getDateFromTimestamp(data.createdAt),
                    clients: client,
                    noOfBaskets: basketCount,
                    cost: sum + turnover,
                    status: data.orderCompleted ? 'Collecté' : 'En cours',
                    sortDate: data.createdAt
                });
            }
        })
    );
    sortOrderTable(viewListOne);
    sortOrderTable(viewListTwo);
    return viewListTwo.concat(viewListOne);
};

export const getCurrentMessage = async (uid: string): Promise<MessagesDeProducteurs | void> => {
    const q = query(
        collection(db, 'MessagesDeProducteurs'),
        where('idProducteur', '==', uid),
        orderBy('createdDate', 'desc')
    );
    const querySnapshot = await getDocs(q);
    if (
        querySnapshot.docs.length > 0 &&
        IsDateWithin24Hours(querySnapshot.docs[0].data().createdDate)
    ) {
        return querySnapshot.docs[0].data();
    }
};

export const deleteCurrentMessage = async (messageId: string): Promise<void> => {
    const docRef = doc(db, 'MessagesDeProducteurs', messageId);
    const docSnap = await getDoc(docRef.withConverter(MessagesDeProducteursConverter));
    if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.photos) {
            await Promise.all(
                data.photos.map(async (photo) => {
                    if (photo && typeof photo === 'string') {
                        await deleteImageFile(photo);
                    }
                })
            );
        }
    }
    await deleteDoc(doc(db, 'MessagesDeProducteurs', messageId));
};

export const getGraphData = async (
    t: TFunction,
    orderData: Commandes[],
    id: string
): Promise<number[]> => {
    const producer = await getProducer(id);
    const viewList: number[] = [0, 0, 0, 0, 0, 0, 0];
    const daysList: number[] = [];
    let count = 1;
    orderData.forEach((item) => {
        if (item.pickupDay) {
            const day = item.pickupDay.toString().split(' ')[0];
            const dayIndex = [
                t('sunday'),
                t('monday'),
                t('tuesday'),
                t('wednesday'),
                t('thursday'),
                t('friday'),
                t('saturday')
            ].indexOf(day);
            daysList.push(dayIndex);
        }
    });
    const startDate = producer?.createdAt?.toDate();
    if (startDate) {
        count = weeksBetween(startDate, new Date());
        for (let i = 0; i < 7; i += 1) {
            daysList.forEach((day) => {
                if (day === i) {
                    viewList[i] += 1;
                }
            });
        }
        for (let i = 0; i < 7; i += 1) {
            if (viewList[i] !== 0) {
                const value = isDayIncluded(startDate.getDay(), i);
                const weeks = value ? count : count - 1;
                viewList[i] = Math.round(viewList[i] / (weeks > 0 ? weeks : 1));
            }
        }
    }
    const [, ...newList] = viewList;
    newList.push(viewList[0]);
    return newList;
};

export const updateBasketStatus = async (id: string, status: boolean): Promise<Paniers | void> => {
    await updateDoc(doc(db, 'Paniers', id), {
        estPublie: status
    });
};

export const deleteSpecificBasket = async (basketId: string): Promise<void> => {
    await updateDoc(doc(db, 'Paniers', basketId), {
        estSupprime: true
    });
};

export const saveBasket = async (data: Paniers): Promise<void | string> => {
    const content = data;
    if (content.photoUrl) {
        const imageList: string[] = [];
        await Promise.all(
            content.photoUrl.map(async (item) => {
                if (typeof item === 'object' && item) {
                    const link = await uploadImage(item);
                    imageList.push(link);
                } else {
                    imageList.push(item);
                }
            })
        );
        content.photoUrl = imageList;
    }
    if (content.idPanier) {
        await updateDoc(doc(db, 'Paniers', content.idPanier), {
            idProducteur: content.idProducteur,
            titre: content.titre,
            poids: content.poids,
            prix: content.prix,
            quantite: content.quantite,
            photoUrl: content.photoUrl,
            detail: content.detail,
            estPublie: true,
            producteurPartenaire: content.producteurPartenaire ?? []
        });
        return content.idPanier;
    } else {
        const paniers = await addDoc(collection(db, 'Paniers'), {
            idProducteur: content.idProducteur,
            titre: content.titre,
            poids: content.poids,
            prix: content.prix,
            quantite: content.quantite,
            photoUrl: content.photoUrl,
            detail: content.detail,
            estPublie: true,
            producteurPartenaire: content.producteurPartenaire ?? [],
            estSupprime: false
        });
        await updateDoc(doc(db, 'Paniers', paniers.id), {
            idPanier: paniers.id
        });
        return paniers.id;
    }
};

export const saveMessage = async (data: MessagesDeProducteurs): Promise<void | string> => {
    const content = data;
    if (content.photos) {
        const imageList: string[] = [];
        await Promise.all(
            content.photos.map(async (item) => {
                if (typeof item === 'object' && item) {
                    const link = await uploadImage(item);
                    imageList.push(link);
                }
            })
        );
        content.photos = imageList;
    }
    content.createdDate = Timestamp.now();
    const messagesDeProducteurs = await addDoc(collection(db, 'MessagesDeProducteurs'), {
        idProducteur: content.idProducteur,
        titre: content.titre,
        description: content.description,
        photos: content.photos,
        createdDate: content.createdDate
    });
    await updateDoc(doc(db, 'MessagesDeProducteurs', messagesDeProducteurs.id), {
        idMessagesDeProducteur: messagesDeProducteurs.id
    });
    return messagesDeProducteurs.id;
};

export const messageCountCurrentMonth = async (uid: string): Promise<number> => {
    const orderList: MessagesDeProducteurs[] = [];
    const q = query(
        collection(db, 'MessagesDeProducteurs'),
        where('idProducteur', '==', uid),
        orderBy('createdDate', 'desc')
    );
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (isDateInThisMonth(data.createdDate)) {
            orderList.push(data);
        }
    });
    return orderList.length;
};

export const updateFarm = async (data: Producteurs): Promise<void | string> => {
    const content = data;
    if (content.illustrationUrl) {
        if (typeof content.illustrationUrl === 'object' && content.illustrationUrl) {
            const link = await uploadImage(content.illustrationUrl);
            content.illustrationUrl = link;
        }
    }
    if (content.idProducteur) {
        await updateDoc(doc(db, 'Producteurs', content.idProducteur), {
            idProducteur: content.idProducteur,
            mail: content.mail,
            nom: content.nom,
            prenom: content.prenom,
            nomFerme: content.nomFerme,
            telephone: content.telephone,
            adresse: content.adresse,
            infoFerme: content.infoFerme ?? '',
            illustrationUrl: content.illustrationUrl ?? '',
            panierRecuperationMethode: content.panierRecuperationMethode ?? '',
            coordinates: content.coordinates
        });
    }
};

export const updateProducer = async (id: string, data: Collections): Promise<Paniers | void> => {
    await updateDoc(doc(db, 'Producteurs', id), {
        collections: data
    });
};

export const getClients = async (): Promise<Clients[]> => {
    const orderList: Clients[] = [];
    const querySnapshot = await getDocs(collection(db, 'Clients').withConverter(ClientsConverter));
    querySnapshot.forEach((docSnap) => {
        if (docSnap.data()) {
            orderList.push(docSnap.data());
        }
    });
    return orderList;
};

export const getOrderForClient = (orderData: Commandes[], client: string): Commandes[] => {
    const result: Commandes[] = [];
    orderData.forEach((order) => {
        if (order.clientId === client) {
            result.push(order);
        }
    });
    return result;
};

export const getClientSatisfaction = async (
    orders: Commandes[],
    clientId: string
): Promise<Satisfaction | undefined> => {
    let disliked = 0;
    let liked = 0;
    let neutral = 0;
    await Promise.all(
        orders.map(async (order) => {
            if (order.baskets) {
                await Promise.all(
                    order.baskets.map(async (item) => {
                        if (item.idpanier) {
                            const basket = await getSpecificBasket(item.idpanier);
                            if (basket) {
                                basket.satisfaction?.map((data) => {
                                    if (
                                        data.idClient === clientId &&
                                        data.dislikedCount &&
                                        data.lovedCount &&
                                        data.neutralCount
                                    ) {
                                        disliked += parseInt(`${data.dislikedCount}`);
                                        liked += parseInt(`${data.lovedCount}`);
                                        neutral += parseInt(`${data.neutralCount}`);
                                    }
                                });
                            }
                        }
                    })
                );
            }
        })
    );
    if (liked > disliked && liked > neutral) {
        return Satisfaction.LOVED;
    } else if (disliked > liked && disliked > neutral) {
        return Satisfaction.DISLIKED;
    } else if (neutral > disliked && neutral > liked) {
        return Satisfaction.NEUTRAL;
    }
};

export const getClientTableData = async (uid: string): Promise<ClientTableData[]> => {
    const orderData = await getOrders(uid);
    const clients = await getClients();
    const tableData: ClientTableData[] = [];
    await Promise.all(
        clients.map(async (client) => {
            const result = getOrderForClient(orderData, client.idClient);
            const satisfaction = await getClientSatisfaction(result, client.idClient);
            if (result.length > 0) {
                const date = getLatestDate(result);
                tableData.push({
                    sortDate: date,
                    clientName: `${client?.prenom} ${client?.nom}`,
                    orderDate: getDateFromTimestamp(date) ?? '',
                    totalAmount: getTotalAmount(result),
                    averageAmount: getAverageAmount(result),
                    numberOfOrders: result.length,
                    satisfaction
                });
            }
        })
    );
    return sortByDate(tableData);
};

export const getStripeId = async (uid: string): Promise<string | void> => {
    const user = await getProducer(uid);
    if (user && user.stripeId) {
        if (user.stripeId) {
            return amosubscription(user.stripeId);
        }
    }
};

export const getSpecificClient = async (clientId: string): Promise<Clients | void> => {
    const docRef = doc(db, 'Clients', clientId);
    const docSnap = await getDoc(docRef.withConverter(ClientsConverter));
    if (docSnap.exists()) {
        return docSnap.data();
    }
};

export const getSharedBasketModalDetails = async (
    partagePanier: PartagePanier[]
): Promise<BasketGroupData[] | void> => {
    let basketList: BasketDetails[] = [];
    const list: BasketGroupData[] = [];
    await Promise.all(
        partagePanier.map(async (item) => {
            if (item.idPanier) {
                await Promise.all(
                    item.idPanier.map(async (data) => {
                        const basket = await getSpecificBasket(data.idpanier);
                        if (basket) {
                            basketList.push({
                                basket,
                                quantity: data.quantite
                            });
                        }
                    })
                );
            }
            if (item.idClient) {
                const client = await getSpecificClient(item.idClient);
                if (client) {
                    list.push({
                        idPanier: basketList,
                        idClient: client
                    });
                    basketList = [];
                }
            }
        })
    );
    return list;
};

export const getSharedBasketDetails = async (ids: GroupOrderIds[]): Promise<PartagePanier[]> => {
    const list: PartagePanier[] = [];
    await Promise.all(
        ids.map(async (id) => {
            const item = await getSpecificOrder(id.orderId);
            if (item && item.baskets) {
                list.push({
                    idPanier: item.baskets,
                    idClient: item.clientId
                });
            }
        })
    );
    return list;
};

export const getStatisticsData = async (uid: string): Promise<IndicatorViewModel> => {
    const list: Commandes[] = await getAllOrders();
    const totalOrders: Commandes[] = [];
    const clientIdList: string[] = [];
    let turnover = 0;
    let satisfaction: OrderSatisfaction[] = [];
    await Promise.all(
        list.map(async (data) => {
            if (data.paymentSuccessful && data.fermeId === uid) {
                totalOrders.push(data);
                if (data.clientId && !clientIdList.includes(data.clientId)) {
                    clientIdList.push(data.clientId);
                }
                if (data.baskets) {
                    await Promise.all(
                        data.baskets.map(async (item) => {
                            const basket = await getSpecificBasket(item.idpanier);
                            if (basket) {
                                turnover += item.prix * item.quantite;
                                if (basket.satisfaction) {
                                    satisfaction = satisfaction.concat(basket.satisfaction);
                                }
                            }
                        })
                    );
                }
            }
        })
    );
    return {
        basketSold: totalOrders.length,
        clients: clientIdList.length,
        review: getReviewDetails(satisfaction),
        turnOver: turnover
    };
};

export const getBasketStatisticsData = async (
    basket: Paniers
): Promise<BasketIndicatorViewModel> => {
    const list: Commandes[] = await getAllOrders();
    const basketIdList: string[] = [];
    let revenue = 0;
    await Promise.all(
        list.map(async (data) => {
            if (data.paymentSuccessful) {
                if (data.baskets) {
                    data.baskets.forEach(async (item) => {
                        if (item.idpanier === basket.idPanier) {
                            revenue += item.prix * item.quantite;
                            basketIdList.push(item.idpanier);
                        }
                    });
                }
            }
        })
    );
    return {
        sales: basketIdList.length ?? 0,
        review: getReviewDetails(basket.satisfaction ?? []),
        cartRevenue: revenue
    };
};

export const getGroupClients = async (
    clientId: string,
    id: string,
    orderIds?: GroupOrderIds[],
    orderStatus?: GroupMemberStatus[]
): Promise<BasketGroupData[]> => {
    const list: BasketGroupData[] = [];
    const members =
        orderStatus &&
        orderStatus.filter((item) => {
            return item.clientId !== clientId;
        });
    if (members) {
        await Promise.all(
            members.map(async (member) => {
                const statusObject =
                    orderStatus && orderStatus.find((obj) => obj.clientId === member.clientId);
                if (statusObject?.orderStatus !== Status.REJECTED) {
                    const basketList: BasketDetails[] = [];
                    const orderMade =
                        orderIds && orderIds.find((obj) => obj.clientId === member.clientId);
                    if (orderMade) {
                        const order = await getSpecificOrder(orderMade.orderId);
                        if (order && order.baskets) {
                            await Promise.all(
                                order.baskets.map(async (item) => {
                                    const basket = await getSpecificBasket(item.idpanier);
                                    if (basket) {
                                        basketList.push({ basket, quantity: item.quantite });
                                    }
                                })
                            );
                        }
                    }
                    const client = await getSpecificClient(member.clientId);
                    if (client) {
                        list.push({
                            idPanier: basketList,
                            idClient: client
                        });
                    }
                }
            })
        );
    }
    return list;
};

export const getUndeletedBaskets = async (uid: string): Promise<Paniers[]> => {
    const paniersList: Paniers[] = [];
    const querySnapshot = await getDocs(collection(db, 'Paniers').withConverter(PaniersConverter));
    querySnapshot.forEach((docSnap) => {
        if (docSnap.data() && docSnap.data().idProducteur === uid && !docSnap.data().estSupprime) {
            paniersList.push(docSnap.data());
        }
    });
    return paniersList;
};
